import React, { useState } from 'react';
import './AvailabilitySection.css';
import { FaBed } from 'react-icons/fa';
import CustomCalendar from "./CustomCalendar";
import TimeSelector from "./TimeSelector";
import { useNavigate } from 'react-router-dom';

export default function AvailabilitySection({ selectedPackage }) {
  const navigate = useNavigate();
  const [beds, setBeds] = useState(1);
  const [selectedRange, setSelectedRange] = useState([null, null]);
  const [checkInTime, setCheckInTime] = useState('');
  const [checkOutTime, setCheckOutTime] = useState('');

  // Convert selected day numbers to real dates for display
  const [start, end] = selectedRange;
  // We'll extract the month/year from the selectedRange if possible
  // CustomCalendar should expose selected month/year for start/end
  // For now, fallback to today if not available
  const [calendarMeta, setCalendarMeta] = useState({ startMonth: null, startYear: null, endMonth: null, endYear: null });

  // Check if selected package is hourly
  const isHourlyPackage = selectedPackage?.plan === 'Hourly Plan';

  // Auto-calculate checkout time based on hourly package duration
  const calculateCheckOutTime = (checkInTime, duration) => {
    if (!checkInTime || !duration) return '';
    
    const [hours, minutes] = checkInTime.split(':').map(Number);
    const checkInDate = new Date();
    checkInDate.setHours(hours, minutes, 0, 0);
    
    let hoursToAdd = 0;
    if (duration === '1 Hour') hoursToAdd = 1;
    else if (duration === '3 Hours') hoursToAdd = 3;
    else if (duration === '5 Hours') hoursToAdd = 5;
    
    const checkOutDate = new Date(checkInDate.getTime() + (hoursToAdd * 60 * 60 * 1000));
    return `${checkOutDate.getHours().toString().padStart(2, '0')}:${checkOutDate.getMinutes().toString().padStart(2, '0')}`;
  };

  // Handle check-in time change for hourly packages
  const handleCheckInTimeChange = (time) => {
    setCheckInTime(time);
    if (isHourlyPackage && selectedPackage?.duration) {
      const calculatedCheckOut = calculateCheckOutTime(time, selectedPackage.duration);
      setCheckOutTime(calculatedCheckOut);
    }
  };

  function handleCalendarRange(range, meta) {
    setSelectedRange(range);
    if (meta) setCalendarMeta(meta);
  }

  function formatDate(day, type = 'start') {
    if (!day) return 'Select date';
    let year = calendarMeta[`${type}Year`];
    let month = calendarMeta[`${type}Month`];
    if (year == null || month == null) {
      const today = new Date();
      year = today.getFullYear();
      month = today.getMonth();
    }
    const d = new Date(year, month, day);
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // Helpers to build real Date from day + calendar meta
  const getRealDate = (day, type) => {
    if (!day) return null;
    let year = calendarMeta[`${type}Year`];
    let month = calendarMeta[`${type}Month`];
    if (year == null || month == null) {
      const today = new Date();
      year = today.getFullYear();
      month = today.getMonth();
    }
    return new Date(year, month, day);
  };

  // Calculate nights across months using real dates
  let nights = 0;
  let startDate = null;
  let endDate = null;
  if (isHourlyPackage) {
    nights = start ? 1 : 0;
    startDate = getRealDate(start, 'start');
    endDate = startDate;
  } else {
    startDate = getRealDate(start, 'start');
    endDate = getRealDate(end, 'end');
    if (startDate && endDate && endDate > startDate) {
      const msPerDay = 1000 * 60 * 60 * 24;
      nights = Math.round((endDate - startDate) / msPerDay);
    }
  }
  
  const pricePerBed = selectedPackage?.price || 599;

  // Infer package if user selects weekly/monthly durations without choosing a package card
  // Weekly: 6–7 nights (covering common user expectations)
  // Monthly: 30–31 nights (covering calendar month spans like Oct 2 → Nov 2)
  // Determine package by thresholds to guarantee correct pricing on date-only selection
  const thresholdPackage = !isHourlyPackage
    ? ((nights >= 30)
        ? { plan: 'Monthly Plan', duration: '30 Days', price: 11999 }
        : (nights >= 6)
          ? { plan: 'Weekly Plan', duration: '7 Days', price: 3499 }
          : null)
    : null;

  const inferredPackage = !selectedPackage ? thresholdPackage : null;

  const effectivePackage = selectedPackage || inferredPackage;

  let total = 0;
  if (isHourlyPackage && selectedPackage) {
    // For hourly packages, use package price directly
    total = beds * selectedPackage.price;
  } else if (!isHourlyPackage && nights >= 30) {
    // Monthly base (30 nights) + extras at ₹599/bed/night
    const extraNights = Math.max(0, nights - 30);
    total = beds * (11999 + extraNights * 599);
  } else if (!isHourlyPackage && nights >= 6) {
    // Weekly base (6 nights / 7 days) + extras at ₹599/bed/night
    const extraNights = Math.max(0, nights - 6);
    total = beds * (3499 + extraNights * 599);
  } else {
    // Regular per-night pricing
    total = beds * nights * pricePerBed;
  }

  const isAvailable = isHourlyPackage 
    ? !!(start && checkInTime && beds > 0)
    : !!(startDate && endDate && nights > 0 && beds > 0);
  
  const handleBookNow = () => {
    // Build common params
    const params = new URLSearchParams({
      beds: beds,
      start: formatDate(start, 'start'),
      end: isHourlyPackage ? formatDate(start, 'start') : formatDate(end, 'end'),
      nights: nights,
      price: pricePerBed,
      checkInTime: checkInTime,
      checkOutTime: checkOutTime
    }).toString();

    // Use navigate so we can pass state with inferred package (if any)
    if (effectivePackage) {
      navigate(`/booking?${params}`, { state: { package: effectivePackage } });
    } else {
      navigate(`/booking?${params}`);
    }
  };

  return (
    <>
      <section className="availability-section">
      <h2 className="availability-title">Check Availability</h2>
      <p className="availability-subtitle">
        {isHourlyPackage 
          ? `Select your date and check-in time for ${selectedPackage?.duration || 'hourly'} stay`
          : 'Select your check-in and check-out dates, choose number of beds'
        }
      </p>
      <div className="availability-content">
        <div className="calendar-placeholder">
          <CustomCalendar 
            selectedRange={selectedRange} 
            onSelectRange={(range, meta) => handleCalendarRange(range, meta)}
            isHourlyPackage={isHourlyPackage}
          />
        </div>
        <div className="booking-summary compact">
          <div className="booking-summary-heading">Booking Summary</div>
          <div className="beds-selector">
            <span className="summary-label">Number of Beds</span>
            <div className="bed-controls">
              <button onClick={() => setBeds(b => Math.max(1, b - 1))}>-</button>
              <span className="bed-count"><FaBed /> {beds}</span>
              <button onClick={() => setBeds(b => b + 1)}>+</button>
            </div>
          </div>
          
          {/* Time selectors for hourly packages */}
          {isHourlyPackage && start && (
            <div className="time-selectors">
              <TimeSelector 
                selectedTime={checkInTime}
                onTimeChange={handleCheckInTimeChange}
                label="Check-in Time"
              />
              {checkOutTime && (
                <div className="checkout-time-display">
                  <span className="summary-label">Check-out Time</span>
                  <span className="summary-value">
                    {new Date(`2000-01-01T${checkOutTime}`).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true
                    })}
                  </span>
                </div>
              )}
            </div>
          )}
          
          <div className="summary-dates">
            <div className="summary-date-row">
              <span className="summary-label">Check-in</span>
              <span className="summary-value">{formatDate(start, 'start')}</span>
            </div>
            {!isHourlyPackage && (
              <div className="summary-date-row">
                <span className="summary-label">Check-out</span>
                <span className="summary-value">{formatDate(end, 'end')}</span>
              </div>
            )}
          </div>
          {isAvailable && (
            <>
              <hr className="summary-divider" />
              <div className="summary-breakdown">
                <div className="summary-breakdown-row">
                  <span>
                    {isHourlyPackage 
                      ? `${beds} Bed × ${selectedPackage?.duration || '1 Hour'}`
                      : `${beds} Bed × ${nights} night${nights !== 1 ? 's' : ''}`
                    }
                  </span>
                  <span className="summary-breakdown-price">₹{total}</span>
                </div>
                <div className="summary-breakdown-sub">
                  {isHourlyPackage 
                    ? `₹${pricePerBed} per Bed for ${selectedPackage?.duration || '1 Hour'}`
                    : (nights >= 30)
                      ? `Monthly: ₹11999 base + ₹599 × ${Math.max(0, nights - 30)} extra night(s) per Bed`
                      : (nights >= 6)
                        ? `Weekly: ₹3499 base + ₹599 × ${Math.max(0, nights - 6)} extra night(s) per Bed`
                        : `₹${pricePerBed} per Bed per night`
                  }
                </div>
              </div>
              <hr className="summary-divider" />
              <div className="summary-total-row">
                <span className="summary-total-label">Total</span>
                <span className="summary-total-value">₹ {total}</span>
              </div>
              <button className="book-btn fullwidth" onClick={handleBookNow}>
                Book Now
              </button>
            </>
          )}
        </div>
      </div>
    </section>
    </>

  );
}
